/// <reference types="vite/client" />
/**
 * What this build needs from its environment, checked once at startup.
 *
 * The settings live in `.env` locally and in build args on CI (`.github/workflows/build.yml`),
 * so they are exactly the values nobody has in version control and everybody assumes are there.
 * When one is absent nothing announces it: the app starts, and the failure surfaces somewhere far
 * away wearing a different bug's clothes — a dead API reads as "the backend is down", a missing
 * Google client id reads as "Google is broken".
 *
 * Two levels, because they fail differently:
 *
 * REQUIRED stops the app. `VITE_API` because every request would go nowhere. `VITE_BASE_URL`
 * because `bible_next/lib/feature_flags.ts` decides "is this production?" by comparing it to the
 * production address — absent, the comparison says "not production" and the unfinished AI assistant
 * would appear on the live site. A hard stop is the lesser failure.
 *
 * FEATURES only warn. A missing push key must not take the site down with it; it takes push down,
 * and the console says which feature is now dead and which variable to set.
 */

export interface FeatureRequirement {
	/** What stops working, in the words someone would use to report it missing. */
	feature: string;
	vars: string[];
}

export const REQUIRED_VARS = ["VITE_API", "VITE_BASE_URL"] as const;

export const FEATURE_VARS: FeatureRequirement[] = [
	{ feature: "Sign in with Google", vars: ["VITE_GOOGLE_CLIENT_ID"] },
	{ feature: "AI Bible search (reCAPTCHA)", vars: ["VITE_RECAPTCHA_KEY"] },
	{ feature: "Chats (CometChat)", vars: ["VITE_COMETCHAT_APP_ID", "VITE_COMETCHAT_REGION"] },
	{ feature: "Push notifications", vars: ["VITE_FIREBASE_VAPID_KEY"] },
];

export interface EnvReport {
	missingRequired: string[];
	brokenFeatures: FeatureRequirement[];
}

// An unset CI secret arrives as an empty string rather than as undefined, and "" is exactly as
// unusable as absent — a baseURL of "" sends every request to the current origin.
const isMissing = (value: unknown): boolean =>
	value === undefined || value === null || (typeof value === "string" && value.trim() === "");

export const checkEnvContract = (env: Record<string, unknown>): EnvReport => ({
	missingRequired: REQUIRED_VARS.filter(name => isMissing(env[name])),
	brokenFeatures: FEATURE_VARS.map(({ feature, vars }) => ({
		feature,
		vars: vars.filter(name => isMissing(env[name])),
	})).filter(({ vars }) => vars.length > 0),
});

/** One grouped warning, so a console with other noise in it still shows the whole picture. */
export const warnAboutBrokenFeatures = (brokenFeatures: FeatureRequirement[]): void => {
	if (brokenFeatures.length === 0) return;

	const lines = brokenFeatures.map(({ feature, vars }) => `  ${feature} — set ${vars.join(", ")}`);
	console.warn(
		`[env] Some features are disabled because their settings are missing:\n${lines.join("\n")}`,
	);
};
